import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';
import { formatCurrency, formatDate } from '../../utils/formatUtils';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div<{ type?: string }>`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  text-align: center;
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  .amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${props => props.type === 'balance' ? 'var(--success-color)' : 
      props.type === 'pending' ? 'var(--warning-color)' : 
      props.type === 'spent' ? 'var(--danger-color)' : 'var(--text-color)'};
  }
  
  .label {
    font-size: 0.85rem;
    color: var(--dark-gray);
    margin-top: 0.5rem;
  }
`;

const StatusBadge = styled(Badge)`
  min-width: 100px;
  text-align: center;
`;

const AmountText = styled.span`
  font-weight: 500;
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;

const PaymentForm = styled.div`
  background: var(--light-gray);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-top: 1rem;
`;

const AccountantOfficeManagement: React.FC = () => {
  const { user } = useAuth();
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [companyBalance, setCompanyBalance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: '',
    reference: ''
  });
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load company balance
      const balanceResponse = await apiService.getCompanyBalance();
      setCompanyBalance(balanceResponse.data);
      
      // Load pending office budget requests
      const requestsResponse = await apiService.getOfficeBudgetRequests({ 
        status: 'Approved by MD',
        limit: 1000 
      });
      setPendingRequests(requestsResponse.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayment = async (requestId: string) => {
    try {
      if (!paymentForm.paymentMethod || !paymentForm.reference) {
        setDialog({
          isOpen: true,
          title: 'Validation Error',
          message: 'Please fill in all payment details.',
          type: 'error'
        });
        return;
      }

      const response = await apiService.processOfficeBudgetPayment(requestId, paymentForm);
      
      setDialog({
        isOpen: true,
        title: 'Payment Processed',
        message: `Payment of ${formatCurrency(response.data.request.amount)} has been processed successfully. New balance: ${formatCurrency(response.data.newBalance)}`,
        type: 'success'
      });

      // Reset form and reload data
      setPaymentForm({ paymentMethod: '', reference: '' });
      setSelectedRequest(null);
      await loadData();
    } catch (error: any) {
      setDialog({
        isOpen: true,
        title: 'Payment Error',
        message: error.message || 'Failed to process payment',
        type: 'error'
      });
    }
  };

  // Use the imported formatCurrency and formatDate functions

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Approved by MD':
        return 'warning';
      case 'Paid':
        return 'success';
      case 'Rejected':
        return 'danger';
      default:
        return 'info';
    }
  };

  if (loading) {
    return <div>Loading office management data...</div>;
  }

  return (
    <div>
      <PageHeader>
        <div>
          <h1 className="page-title">Office Management</h1>
          <p style={{ color: 'var(--dark-gray)', margin: '0.5rem 0 0 0' }}>
            Manage office budget payments and company balance
          </p>
        </div>
      </PageHeader>

      {companyBalance && (
        <Summary>
          <SummaryCard type="balance">
            <h3>Current Balance</h3>
            <div className="amount">{formatCurrency(companyBalance.currentBalance)}</div>
            <div className="label">Available funds</div>
          </SummaryCard>
          
          <SummaryCard type="pending">
            <h3>Pending Payments</h3>
            <div className="amount">{formatCurrency(pendingRequests.reduce((sum, req) => sum + req.amount, 0))}</div>
            <div className="label">Awaiting processing</div>
          </SummaryCard>
          
          <SummaryCard type="spent">
            <h3>Total Spent</h3>
            <div className="amount">{formatCurrency(companyBalance.totalSpent)}</div>
            <div className="label">This period</div>
          </SummaryCard>
          
          <SummaryCard>
            <h3>Remaining Budget</h3>
            <div className="amount" style={{ 
              color: companyBalance.remainingBudget >= 0 ? 'var(--success-color)' : 'var(--danger-color)' 
            }}>
              {formatCurrency(companyBalance.remainingBudget)}
            </div>
            <div className="label">Budget vs spent</div>
          </SummaryCard>
        </Summary>
      )}

      <Card>
        <CardHeader>
          <h3>Pending Office Budget Payments</h3>
        </CardHeader>
        <CardBody>
          {pendingRequests.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Request ID</TableHeader>
                    <TableHeader>Purpose</TableHeader>
                    <TableHeader>Requester</TableHeader>
                    <TableHeader>Amount</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Created</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {pendingRequests.map((request) => (
                    <TableRow key={request._id}>
                      <TableCell>{request._id}</TableCell>
                      <TableCell>{request.purpose}</TableCell>
                      <TableCell>{request.requester}</TableCell>
                      <TableCell>
                        <AmountText>{formatCurrency(request.amount)}</AmountText>
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={getStatusVariant(request.status)}>
                          {request.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <DateText>{formatDate(request.createdAt)}</DateText>
                      </TableCell>
                      <TableCell>
                        <FlexContainer gap="0.5rem">
                          <Button 
                            onClick={() => setSelectedRequest(request)}
                            disabled={companyBalance?.currentBalance < request.amount}
                          >
                            Process Payment
                          </Button>
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>No pending office budget payments to process.</p>
            </div>
          )}
        </CardBody>
      </Card>

      {selectedRequest && (
        <Card style={{ marginTop: '1.5rem' }}>
          <CardHeader>
            <h3>Process Payment - {selectedRequest._id}</h3>
          </CardHeader>
          <CardBody>
            <div style={{ marginBottom: '1rem' }}>
              <h4>Request Details</h4>
              <p><strong>Purpose:</strong> {selectedRequest.purpose}</p>
              <p><strong>Amount:</strong> {formatCurrency(selectedRequest.amount)}</p>
              <p><strong>Requester:</strong> {selectedRequest.requester}</p>
              <p><strong>Justification:</strong> {selectedRequest.justification}</p>
            </div>

            <PaymentForm>
              <h4>Payment Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <label>Payment Method</label>
                  <Select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  >
                    <option value="">Select Method</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Cash">Cash</option>
                    <option value="Check">Check</option>
                    <option value="Mobile Money">Mobile Money</option>
                  </Select>
                </div>
                <div>
                  <label>Reference Number</label>
                  <Input
                    type="text"
                    placeholder="Enter reference number"
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                  />
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <p><strong>Current Balance:</strong> {formatCurrency(companyBalance?.currentBalance || 0)}</p>
                <p><strong>Payment Amount:</strong> {formatCurrency(selectedRequest.amount)}</p>
                <p><strong>Remaining After Payment:</strong> {formatCurrency((companyBalance?.currentBalance || 0) - selectedRequest.amount)}</p>
              </div>

              <FlexContainer gap="1rem" style={{ marginTop: '1rem' }}>
                <Button 
                  onClick={() => handleProcessPayment(selectedRequest._id)}
                  disabled={!paymentForm.paymentMethod || !paymentForm.reference || (companyBalance?.currentBalance || 0) < selectedRequest.amount}
                >
                  Process Payment
                </Button>
                <OutlineButton onClick={() => setSelectedRequest(null)}>
                  Cancel
                </OutlineButton>
              </FlexContainer>
            </PaymentForm>
          </CardBody>
        </Card>
      )}

      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={() => setDialog(prev => ({ ...prev, isOpen: false }))}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
};

export default AccountantOfficeManagement; 